'use client';

import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useLocale, useTranslations } from 'next-intl';
import { Link } from '@/components/ui/link';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { Skeleton } from '@/components/ui/Skeleton';
import { MatchScoreSheet } from '@/components/match/MatchScoreSheet';
import { MatchStrip } from '@/components/match/MatchStrip';
import { useOutcomeLabel } from '@/components/match/matchOutcomeLabel';
import { useLiveMatch } from '@/hooks/useLiveMatch';
import { formatTime } from '@/lib/utils';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import {
  cardState,
  matchCardStatus,
  type MatchPhase,
} from '@/app/[locale]/event/[eventId]/libs/matchCardStatus';
import { initialsFromLabel } from '@/app/[locale]/event/[eventId]/libs/participantLabel';
import type { WatchRow } from '@/app/[locale]/event/[eventId]/libs/watchMatches';
import { sheetGamesFor } from '@/app/[locale]/match/libs/scoreSheet';
import type { PublicMatch } from '@/app/[locale]/event/[eventId]/[slug]/(tabs)/schedule/libs/scheduleStatus';
import { NameText } from '../../components/NameText';

/** A goal-scored (futsal) match keeps its score in `app_state`, never as rallies. */
const isGoalScored = (appState: unknown): boolean =>
  (appState as { scoringAppType?: unknown } | null)?.scoringAppType === 'futsal-show';

/**
 * The sheet, and the only place `useLiveMatch` runs: it is mounted for a row that can have a
 * sheet, so no other row costs a request. Names come from the Watch row (already gated), never
 * from the fetched match.
 */
function PanelSheet({
  matchId,
  sides,
  onGoalScored,
}: {
  matchId: string;
  sides: [string, string];
  onGoalScored: () => void;
}): ReactElement | null {
  const { match, status } = useLiveMatch({ matchId });
  const goalScored = match ? isGoalScored(match.app_state) : false;
  // A goal-scored match has no rally record, and one finishing while selected would poll until the
  // panel unmounts. The first response is all this child is for: tell the panel, which drops it.
  useEffect(() => {
    if (goalScored) onGoalScored();
  }, [goalScored, onGoalScored]);

  if (status === 'error' || goalScored) return null;
  if (!match) {
    return (
      <div className="border-border w-full min-w-0 border-t p-3">
        <Skeleton.Group>
          <Skeleton.Block height={156} />
        </Skeleton.Group>
      </div>
    );
  }
  const live = match.status === 'in_progress';
  const { sheetGames } = sheetGamesFor(match);
  // The sheet's root is a size container, which collapses in a shrink-to-fit parent: give it width.
  return (
    <div className="w-full min-w-0">
      <MatchScoreSheet
        games={sheetGames}
        sides={sides}
        showServeBox={false}
        density="compact"
        live={live}
      />
    </div>
  );
}

/**
 * The selected match under the Watch video: a strip with a chevron to the scorecard, the names
 * (a button that brings the card into view), the feed link, and the score sheet where one can
 * exist. The caller keys it on the match id.
 */
export function WatchScorecardPanel({
  row,
  onShowCard,
  feedLink,
}: {
  row: WatchRow<PublicMatch>;
  onShowCard: () => void;
  /** The `{platform} ↗` link and the feed's other destinations. */
  feedLink: ReactNode;
}): ReactElement {
  const t = useTranslations('EventWatch');
  const tMatches = useTranslations('Event.matches');
  const tMatchesCommon = useTranslations('Matches');
  const locale = useLocale();
  const outcomeLabel = useOutcomeLabel();
  const [goalScored, setGoalScored] = useState(false);
  const dropSheet = useCallback(() => setGoalScored(true), []);
  const { match } = row;

  const names: [string, string] = [match.players[0] ?? '', match.players[1] ?? ''];
  const tbd = tMatchesCommon('tbd');
  const shown: [string, string] = [names[0] || tbd, names[1] || tbd];

  const winnerless = isWinnerlessRow({ outcome: match.outcome, is_walkover: match.isWalkover });
  const phase: MatchPhase =
    match.status === 'live' ? 'live' : match.status === 'upcoming' ? 'upcoming' : 'finished';
  const state = cardState(winnerless, phase);
  const { statusWord, status } = matchCardStatus({
    state,
    outcomeWord: state === 'winnerless' ? outcomeLabel(match.outcome, match.isWalkover) : null,
    retired: match.outcome === 'retired',
    words: {
      live: tMatches('statusLive'),
      upcoming: tMatches('statusUpcoming'),
      finished: tMatches('statusFinished'),
      retiredShort: tMatchesCommon('retiredShort'),
    },
  });

  // Upcoming, no match row, walkover, not played and dual withdrawal have nothing to show.
  const hasSheet = Boolean(match.matchId) && !winnerless && phase !== 'upcoming' && !goalScored;

  return (
    <div
      data-testid="watch-scorecard-panel"
      className="bg-card border-border-strong w-full min-w-0 overflow-hidden rounded-lg border shadow-sm lg:min-h-28 lg:overflow-y-auto"
    >
      <MatchStrip
        leading={
          <LocalDateTime
            iso={match.scheduledAt}
            locale={locale}
            format={formatTime}
            className="text-muted font-mono text-xs font-semibold tabular-nums"
          />
        }
        division={match.division ?? undefined}
        status={status}
        statusWord={statusWord}
        trailing={
          match.matchId ? (
            <Link
              href={`/match/scorecard/${match.matchId}`}
              target="_blank"
              rel="noopener"
              aria-label={[tMatches('viewScorecard'), shown[0], shown[1]].join(' · ')}
              className="text-muted focus-visible:outline-primary relative z-[1] -mt-1 -mr-2 -mb-4 flex size-11 shrink-0 items-start justify-center self-start pt-2 focus-visible:outline-2 focus-visible:-outline-offset-2"
            >
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          ) : undefined
        }
      />
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 px-3 py-2">
        <button
          type="button"
          onClick={onShowCard}
          title={t('showInList')}
          className={`text-foreground -my-2.5 min-w-0 cursor-pointer py-2.5 text-left text-sm font-semibold ${NAME_WRAP}`}
        >
          <NameText name={shown[0]} /> {t('vs')} <NameText name={shown[1]} />
        </button>
        {feedLink && <div className="flex flex-wrap items-center gap-x-3 gap-y-1">{feedLink}</div>}
      </div>
      {hasSheet && (
        <PanelSheet
          key={match.matchId}
          matchId={match.matchId!}
          sides={[initialsFromLabel(names[0]), initialsFromLabel(names[1])]}
          onGoalScored={dropSheet}
        />
      )}
    </div>
  );
}
